import { IAssistanceReason, IDailyMetric } from './dataByDocumentType.ts';
import { kebabCase } from 'lodash';

const AVERAGE_TIME_PER_DOCUMENT_BEFORE_WORKIST = 270; // seconds
const ASSISTANCE_TIME_DATA_AVAILABLE_MONTHS = 6; // assistance time data only available for last 6 months
export const demoGenerateDailyMetrics = (
    documentType: string,
    startDate: string,
    endDate: string,
    channelIds?: string[]
): IDailyMetric[] => {
    /*
    Generate demo daily metric data for a given date range and optional channel IDs.
    This function uses a seeded random number generator to produce consistent results
    */
    const start = new Date(startDate);
    const end = new Date(endDate);
    const metrics: IDailyMetric[] = [];

    const baseMetric = {
        avgFinished: 60,
        assistedRatio: 0.2,
        discardedRatio: 0.05,
        headerAssistedRatio: 0.2,
        avgLineItems: 8,
        lineItemsAssistedRatio: 0.35,
        avgAssistanceTime: 93,
    };

    const channelMultiplier = channelIds && channelIds.length ? Math.min(1, channelIds.length * 0.5) : 1;

    // Calculate cutoff date for assistance time data availability
    const assistanceTimeCutoffDate = new Date();
    assistanceTimeCutoffDate.setMonth(assistanceTimeCutoffDate.getMonth() - ASSISTANCE_TIME_DATA_AVAILABLE_MONTHS);

    for (let date = new Date(start); date <= end; date.setDate(date.getDate() + 1)) {
        const seed = hashString(documentType + date.toISOString());
        const rng = createSeededRandom(seed);

        const dayOfWeek = date.getDay();
        const weekendFactor = dayOfWeek === 0 ? 0 : dayOfWeek === 6 ? 0.1 : 1;
        const volumeFactor = weekendFactor * channelMultiplier;

        const finishedCount = Math.round(baseMetric.avgFinished * volumeFactor * (0.5 + rng() * 1.0));
        const assistedCount = Math.round(finishedCount * (baseMetric.assistedRatio + (rng() - 0.5) * 0.3));
        const automatedCount = finishedCount - assistedCount;
        const discardedCount = Math.round(finishedCount * (baseMetric.discardedRatio + (rng() - 0.5) * 0.04));
        const deletedCount = Math.round(finishedCount * (0.03 + rng() * 0.07));

        const headerTotalCount = Math.round(finishedCount * (1.2 + rng() * 0.3));
        const headerAssistedCount = Math.round(
            headerTotalCount * (baseMetric.headerAssistedRatio + (rng() - 0.5) * 0.1)
        );
        const headerAutomatedCount = headerTotalCount - headerAssistedCount;
        const headerAutomationRatio = headerTotalCount > 0 ? headerAutomatedCount / headerTotalCount : 0;

        const lineItemsTotalCount = Math.round(finishedCount * (baseMetric.avgLineItems + (rng() - 0.5) * 2));
        const lineItemsAssistedCount = Math.round(
            lineItemsTotalCount * (baseMetric.lineItemsAssistedRatio + (rng() - 0.5) * 0.15)
        );
        const lineItemsAutomatedCount = lineItemsTotalCount - lineItemsAssistedCount;
        const lineItemsAutomationRatio = lineItemsTotalCount > 0 ? lineItemsAutomatedCount / lineItemsTotalCount : 0;

        // Check if assistance time data should be available for this date
        const assistanceTimeDataAvailable = date >= assistanceTimeCutoffDate;

        const medianAssistanceTime =
            assistanceTimeDataAvailable && assistedCount > 0
                ? Math.round(baseMetric.avgAssistanceTime * (0.8 + rng() * 0.4))
                : undefined;

        const totalAssistanceTime =
            assistanceTimeDataAvailable && assistedCount > 0
                ? Math.round(assistedCount * medianAssistanceTime * (1.5 + rng() * 0.8))
                : undefined;

        const totalAssistanceTimeBeforeWorkist =
            assistanceTimeDataAvailable && totalAssistanceTime
                ? Math.round(finishedCount * AVERAGE_TIME_PER_DOCUMENT_BEFORE_WORKIST * (1.5 + rng() * 0.6))
                : undefined;

        const trackedCount =
            assistanceTimeDataAvailable && finishedCount > 0
                ? Math.round(assistedCount * (0.9 + rng() * 0.2))
                : undefined;

        metrics.push({
            date: date.toISOString().split('T')[0],
            finishedCount,
            assistedCount,
            discardedCount,
            deletedCount,
            automatedCount,
            headerAutomatedCount,
            headerAssistedCount,
            headerTotalCount,
            headerAutomationRatio: Number(headerAutomationRatio.toFixed(3)),
            lineItemsAutomatedCount,
            lineItemsAssistedCount,
            lineItemsTotalCount,
            lineItemsAutomationRatio: Number(lineItemsAutomationRatio.toFixed(3)),
            medianAssistanceTime,
            totalAssistanceTime,
            totalAssistanceTimeBeforeWorkist,
            averageAssistanceTimePerDocumentBeforeWorkist: AVERAGE_TIME_PER_DOCUMENT_BEFORE_WORKIST,
            trackedCount,
        });
    }

    return metrics;
};
const randomUUID = (seed?: number) => {
    const rng = seed !== undefined ? createSeededRandom(seed) : Math.random;

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = (rng() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
};

export const demoGenerateAssistanceReasons = (
    documentType: string,
    startDate: string,
    endDate: string,
    channelIds?: string[]
): IAssistanceReason[] => {
    const seed = hashString(documentType + startDate + endDate);
    const rng = createSeededRandom(seed);

    const fieldNames = ['quantity', 'request_number', 'unit', 'request_date', 'client', 'total_amount', 'description'];
    const commonReasons = ['FIELD_UNCONFIDENT', 'FIELD_INVALID'];
    const rareReasons = ['OPTIONAL_NOT_EMPTY', 'VALIDATION_CHECK_FAILED', 'NO_LINE_ITEMS', 'ORDER_MATCHING_FAILED'];

    const reasons = [
        ...commonReasons.map((reason) => ({
            reason,
            fieldName: fieldNames[Math.floor(rng() * fieldNames.length)],
            weight: 0.1 + rng() * 0.3,
            sampleUrls: Array.from(
                { length: 5 },
                (_, index) =>
                    `${window.location.origin}/${kebabCase(documentType)}/${randomUUID(seed + hashString(fieldNames[Math.floor(rng() * fieldNames.length)]) + index)}`
            ),
        })),
        ...rareReasons
            .sort(() => rng() - 0.5)
            .slice(0, 3)
            .map((reason) => ({
                reason,
                fieldName: fieldNames[Math.floor(rng() * fieldNames.length)],
                weight: 0.01 + rng() * 0.04,
                sampleUrls: Array.from(
                    { length: 3 },
                    (_, index) =>
                        `${window.location.origin}/${kebabCase(documentType)}/${randomUUID(seed + hashString(reason) + index)}`
                ),
            })),
    ];

    const totalWeight = reasons.reduce((sum, r) => sum + r.weight, 0);
    let remaining = 1;

    return reasons
        .map((r, i) => {
            const percentage = i === reasons.length - 1 ? remaining : Number((r.weight / totalWeight).toFixed(4));
            remaining -= percentage;
            return { ...r, percentage };
        })
        .map(({ weight, ...r }) => r)
        .sort((a, b) => b.percentage - a.percentage);
};

const hashString = (str: string): number => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash = hash & hash;
    }
    return Math.abs(hash);
};

const createSeededRandom = (seed: number) => {
    let state = seed;
    return () => {
        state = (state * 1664525 + 1013904223) % 4294967296;
        return state / 4294967296;
    };
};
